/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import { useAlert } from 'dashboard/composables';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

// The browser Push API expects the VAPID application server key as a
// BufferSource. The dashboard layout already exposes it as a Uint8Array,
// while API-driven pages can provide a base64url string. Support both formats
// instead of serializing a Uint8Array as "4,37,...", which Chrome rejects.
export const vapidKeyToUint8Array = vapidPublicKey => {
  if (vapidPublicKey instanceof Uint8Array) return vapidPublicKey;

  if (ArrayBuffer.isView(vapidPublicKey)) {
    return new Uint8Array(
      vapidPublicKey.buffer,
      vapidPublicKey.byteOffset,
      vapidPublicKey.byteLength
    );
  }

  if (Array.isArray(vapidPublicKey)) return Uint8Array.from(vapidPublicKey);

  if (typeof vapidPublicKey !== 'string' || !vapidPublicKey.trim()) {
    throw new Error(
      'A chave pública VAPID está ausente ou em formato inválido.'
    );
  }

  const padding = '='.repeat((4 - (vapidPublicKey.length % 4)) % 4);
  const base64 = `${vapidPublicKey}${padding}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawKey = window.atob(base64);

  return Uint8Array.from(rawKey, character => character.charCodeAt(0));
};

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  // The dashboard authenticates API calls with the Rails session. The legacy
  // `cw_d_session_info` cookie is optional (and can be HttpOnly), so checking
  // it here prevents valid logged-in users from ever reaching the API.
  return NotificationSubscriptions.create(
    getPushSubscriptionPayload(subscription)
  );
};

const pushFailureMessage = (message, error) => {
  if (!error) return message;

  const errorName = error.name || error.constructor?.name || 'Error';
  const errorMessage = error.message || String(error);
  return `${message} (${errorName}: ${errorMessage})`;
};

export const registerSubscription = async (
  onSuccess = () => {},
  onError = () => {}
) => {
  const vapidPublicKey = window.lumeratecConfig.vapidPublicKey;
  if (!vapidPublicKey) {
    const message =
      'A chave pública VAPID não foi carregada nesta página. Recarregue e tente novamente.';
    useAlert(message);
    onError(message);
    return false;
  }

  try {
    // `register()` resolves when installation starts, not necessarily when a
    // worker is active. A first-time production visitor therefore needs the
    // readiness barrier before `pushManager.subscribe()` is allowed.
    const registration = await navigator.serviceWorker.register('/sw.js');
    // `serviceWorker.ready` can stay pending when Chrome still has an older
    // registration for the origin, even though the registration returned by
    // `register()` already has an active worker. Prefer that active worker and
    // keep `ready` only as the first-install fallback.
    const serviceWorkerRegistration = registration.active
      ? registration
      : await navigator.serviceWorker.ready;
    const subscription =
      (await serviceWorkerRegistration.pushManager.getSubscription()) ||
      (await serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyToUint8Array(vapidPublicKey),
      }));

    await sendRegistrationToServer(subscription);
    onSuccess();
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Push subscription registration failed:', error);
    const message = pushFailureMessage(
      'O navegador não conseguiu criar a assinatura push',
      error
    );
    useAlert(message);
    onError(message);
    return false;
  }
};

export const unregisterSubscription = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return true;

    // Remove the server record before discarding the endpoint locally. This
    // prevents notifications from being sent to a device the user disabled.
    await NotificationSubscriptions.destroyBrowserPush(subscription.endpoint);
    return subscription.unsubscribe();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Push subscription removal failed:', error);
    useAlert(
      'Não foi possível desativar as notificações neste navegador. Tente novamente.'
    );
    return false;
  }
};

export const requestPushPermissions = async ({
  onSuccess,
  onError = () => {},
}) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    const message = 'Este navegador não suporta notificações desktop.';
    useAlert(message);
    onError(message);
    return false;
  }

  if (Notification.permission === 'denied') {
    const message =
      'As notificações estão bloqueadas para este site. Libere a permissão nas configurações do navegador e tente novamente.';
    useAlert(message);
    onError(message);
    return false;
  }

  let permission;
  try {
    permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
  } catch (error) {
    const message = pushFailureMessage(
      'O navegador falhou ao solicitar a permissão de notificações',
      error
    );
    useAlert(message);
    onError(message);
    return false;
  }

  if (permission !== 'granted') {
    const message =
      permission === 'default'
        ? 'O navegador não exibiu o pedido de notificação. Verifique se o Chrome não está usando avisos discretos e tente permitir o site nas configurações.'
        : 'As notificações não foram ativadas. Autorize-as no navegador para continuar.';
    useAlert(message);
    onError(`${message} (permissão retornada: ${permission})`);
    return false;
  }

  return registerSubscription(onSuccess, onError);
};
